/**
 * Carrier labels almost always print London Smoke & Cure as the return / sender.
 * That address also appears on Spa Shop / market / Bermondsey collection orders, so
 * matching on it false-aligns every local order. Strip return/sender evidence before
 * address / postcode / name / company / city scoring. Order/source identifiers still
 * use the full page text.
 */

/** Known facility / brand return phrases (extend when labels change). */
export const LSC_RETURN_ADDRESS_IGNORE_PHRASES = Object.freeze([
  'london smoke & cure',
  'london smoke and cure',
  'se16 3fj',
  'se163fj',
  'unit 7',
  'arch 7',
  'spa business park',
]);

/**
 * Labels that open a return/sender block on carrier PDFs.
 * Capture runs until a delivery/recipient heading (or end of text).
 */
const RETURN_BLOCK_START_RE =
  /\b(?:return\s*address|returns?\s*to|from|sender|despatched\s*by|dispatched\s*by|shipped\s*from|sender\s*address)\b[\s:.\-]*/i;

const DELIVERY_BLOCK_START_RE =
  /\b(?:ship\s*to|deliver(?:y)?\s*to|delivery\s*address|consignee|recipient|addressee|customer\s*ref)\b[\s:.\-]*/i;

/**
 * Tracked 24 (and similar) print marketing fluff after the return block, then the
 * recipient — with no Ship to / Customer Ref heading. Stop the return wipe here
 * so company / name / postcode stay for scoring.
 */
const RETURN_BLOCK_END_WITHOUT_DELIVERY_RE =
  /\b(?:post\s+by\s+the\s+end|paid\s+and\s+printed|marketplace\s+seller|royal\s+mail:?|uk'?s\s+lowest\s+average|parcel\s+carbon)\b/i;

/**
 * Remove labelled return/sender sections and known LSC return phrases.
 * @param {string} text
 * @returns {string}
 */
export function scrubReturnAddressFromLabelText(text) {
  let out = String(text || '');
  if (!out.trim()) return '';

  // Walk labelled return blocks; keep delivery sections intact.
  let safety = 0;
  while (safety < 8) {
    safety += 1;
    const start = out.search(RETURN_BLOCK_START_RE);
    if (start < 0) break;
    const afterStart = out.slice(start).replace(RETURN_BLOCK_START_RE, '');
    const deliveryRel = afterStart.search(DELIVERY_BLOCK_START_RE);
    if (deliveryRel >= 0) {
      out = `${out.slice(0, start)} ${afterStart.slice(deliveryRel)}`;
      continue;
    }
    const fluffRel = afterStart.search(RETURN_BLOCK_END_WITHOUT_DELIVERY_RE);
    if (fluffRel >= 0) {
      // Drop return + keep marketing+recipient (phrase scrub removes LSC leftovers).
      out = `${out.slice(0, start)} ${afterStart.slice(fluffRel)}`;
      continue;
    }
    // Last resort: cut only through the facility postcode when present, else leave
    // the labelled start in place for phrase scrub (never wipe the whole page tail).
    const facilityEnd = afterStart.search(/\bse16\s*3fj\b/i);
    if (facilityEnd >= 0) {
      const afterFacility = afterStart.slice(facilityEnd).replace(/\bse16\s*3fj\b/i, '');
      out = `${out.slice(0, start)} ${afterFacility}`;
      continue;
    }
    break;
  }

  for (const phrase of LSC_RETURN_ADDRESS_IGNORE_PHRASES) {
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
    out = out.replace(new RegExp(escaped, 'gi'), ' ');
  }

  return out.replace(/\s+/g, ' ').trim();
}

/**
 * True when a match phrase is only London Smoke & Cure return/facility text.
 * Used so company/postcode hits on scrubbed leftovers still get ignored.
 * @param {string} phrase
 */
export function isLondonSmokeReturnAddressPhrase(phrase) {
  const normalizePhrase = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const needle = normalizePhrase(phrase);
  if (!needle) return false;
  return LSC_RETURN_ADDRESS_IGNORE_PHRASES.some((known) => {
    const compactKnown = normalizePhrase(known);
    if (!compactKnown) return false;
    return (
      needle === compactKnown ||
      needle.includes(compactKnown) ||
      compactKnown.includes(needle)
    );
  });
}
